// Where the admin surface answers.
//
// Its own module importing nothing, on auth/endpoints.ts's reasoning exactly:
// rateLimit.ts has to name these urls in order to limit them, and routes.ts has
// to read `clientIp` out of rateLimit.ts, and those two imports in one pair of
// files is a cycle whose failure depends on evaluation order.
//
// SEPARATE FROM identity/routes.ts's ADMIN_ENDPOINTS, WHICH ALREADY OWNS
// /v1/admin/enrolment-codes AND /v1/admin/devices/revoke. Those two are the
// device control plane and they run on the admin token. These are the
// surface a named person administers, and all but bootstrap run on a session.
// Most of them are about people and recordInstruction is argued for at its own
// key. Two objects rather than one
// because the credential differs, and a single ADMIN_ENDPOINTS would invite
// somebody to gate them all the same way — which, in the direction that
// matters, means gating user creation on a shared secret.
//
// THE TWO STATE-CHANGE URLS ARE NOUNS AND NOT VERBS. `.../disabled` and
// `.../revocation` rather than `.../disable` and `.../revoke`, matching
// /v1/auth/second-factor/confirmation. The rate limiter keys its rules on the
// url and knows nothing about methods, so every distinct thing that needs its
// own ceiling needs its own url; naming them for the state they produce keeps
// that from reading as a remote procedure call.

export const PEOPLE_ENDPOINTS = {
  /**
   * The first nova_admin, on the admin token, and only while `identity` is
   * empty. See admin/bootstrap.ts for the whole argument.
   *
   * Its own url rather than a mode of the one below it, because the two want
   * different credentials, different ceilings and different refusals, and a
   * single endpoint that quietly changes which credential it accepts depending
   * on a table's contents is the shape nobody can review.
   */
  bootstrap: "/v1/admin/bootstrap",
  /**
   * The live people of one client. GET lists identities whose client_id is the
   * query — those are the only people who can be instructed_identity_id.
   * POST is the first grant (email, role). Same path as create, different
   * method, the way listClients shares createClient: the rate limiter keys on
   * the url, so both methods share one ceiling.
   */
  listIdentities: "/v1/admin/identities",
  createIdentity: "/v1/admin/identities",
  /**
   * A name correction on a person. Email is not on this write: the mailbox is
   * how they sign in. Same argument as correctClient for having its own url:
   * the rate limiter keys on the url, and GET/POST `/v1/admin/identities`
   * already share a ceiling. PATCH rather than `.../name` because the row is
   * not entering a new state.
   */
  correctIdentity: "/v1/admin/identities/:identityId",
  disableIdentity: "/v1/admin/identities/:identityId/disabled",
  /**
   * Console Team (149). The list, and the three writes the screen makes on one
   * person: their access, an Editor's clients, and the export switch.
   *
   * Nouns under the identity, on `.../disabled`'s reasoning: each is a state the
   * row is put into rather than a verb, and each is its own url so the rate
   * limiter, which keys on the url, gives each its own ceiling. PUT rather than
   * PATCH because each body is the whole of what it sets — `client_ids` is the
   * list, not a change to it.
   */
  team: "/v1/admin/team",
  identityAccess: "/v1/admin/identities/:identityId/access",
  identityClients: "/v1/admin/identities/:identityId/clients",
  identityExport: "/v1/admin/identities/:identityId/export",
  /**
   * The Console invitation mail, sent or sent again from Team (150). A noun,
   * on `.../disabled`'s reasoning: the person is put into the state of having
   * been invited. POST, because each press is a new mail and a new row.
   */
  identityInvitation: "/v1/admin/identities/:identityId/invitation",
  createClient: "/v1/admin/clients",
  /**
   * The live clients a Nova admin can attach a new activation to.
   *
   * Same path as create, different method. A list that lived on GET /v1/activations
   * would be a second opinion about whose shows exist, and that read already
   * refuses to carry counts for an audit reason. This one names companies, not
   * shows, and is how a console offers "existing client" without inventing a
   * third object called a project.
   */
  listClients: "/v1/admin/clients",
  /**
   * The live clients a new activation can be added to, for Add activation:
   * every one for an Admin, the ones they hold for an Editor (Gabe, 11 Oct).
   * Names and ids only. Its own url rather than a mode of listClients, because
   * that one is the Clients page's audited directory with its counts and
   * agreements, and an Admin's alone; a flag that narrowed it for an Editor
   * would be one parameter between an Editor and the whole directory.
   */
  clientChoices: "/v1/admin/client-choices",
  /**
   * A name correction. 010 argued companies rebrand; this is the write.
   *
   * Its own url rather than a PATCH of the collection, because the rate limiter
   * keys on the url and GET/POST `/v1/admin/clients` already share a ceiling.
   * `:clientId` matches `:identityId` and `:grantId`. PATCH rather than a noun
   * like `.../name` because the row is not entering a new state — the name is
   * the same column, corrected.
   */
  correctClient: "/v1/admin/clients/:clientId",
  /**
   * An archival. 010: archived, never deleted; the name is freed for a new row.
   *
   * A noun and not a verb, matching `.../disabled` and `.../revocation`. PATCH
   * of `:clientId` is already the name correction, and the rate limiter keys on
   * the url, so a second write on that path would share a ceiling with a
   * rebrand. Archive is a state the row enters once.
   */
  archiveClient: "/v1/admin/clients/:clientId/archived",
  /**
   * A signed processing agreement, put on file. What lets an export run at all,
   * from 126 and the refusal in export.ts.
   *
   * Its own url on archiveClient's reasoning exactly: PATCH of `:clientId` is
   * the name correction, the rate limiter keys on the url, and recording the
   * document that permits every future disclosure of this client's attendees
   * should not share a ceiling with fixing a spelling. A noun, because the row
   * either has one on file or does not.
   */
  recordAgreement: "/v1/admin/clients/:clientId/agreement",
  createGrant: "/v1/admin/grants",
  revokeGrant: "/v1/admin/grants/:grantId/revocation",
  /**
   * What a client instructed Nova to collect at one activation.
   *
   * The first url here that is not about people, which is a
   * reason to ask whether it belongs in this object. It does, and the answer is
   * the credential: this file's split is "the device control plane runs on the
   * admin token, these run on a session", and an instruction runs on a session
   * for the reason admin/instruction.ts opens with. Grouping it with the
   * publishing urls, which take the token, is what would invite somebody to
   * gate it the same way — and gating this on a shared secret means an
   * instruction that names nobody, which is the whole failure being fixed.
   *
   * A plural noun and no parameter in the path, matching the publishing urls:
   * the rate limiter keys its rules on the url and knows nothing about
   * parameters, so a flat path is one the two lists cannot drift on.
   *
   * GET lists the recorded versions for one activation (query activation_id).
   * POST records a new one. Same path, different method, so the rate limiter
   * shares one ceiling.
   */
  recordInstruction: "/v1/admin/client-instructions",
  /**
   * Working legal-copy draft for one activation. GET lists the newest row.
   * PUT records a new one. Same path, different method, so the rate limiter
   * shares one ceiling. Query and body both carry activation_id.
   */
  legalDraft: "/v1/admin/legal-drafts",
  /**
   * Mailgun and Resend totals, as the providers will say them.
   *
   * Its own url rather than a mode of a report, because this is not attendee
   * data and the rate limiter keys on the url. GET only.
   */
  emailStats: "/v1/admin/email-stats",
  /**
   * Plausible totals for a public site this console is operating.
   *
   * Its own url rather than a mode of a report, because this is not attendee
   * data and the rate limiter keys on the url. GET only.
   */
  siteStats: "/v1/admin/site-stats",
  /**
   * Ride sponsor switches and picture citations. GET reads. PUT records.
   * Same path, different method, one rate-limit ceiling.
   */
  rideSponsor: "/v1/admin/ride-sponsor",
  /**
   * A still for Ride sponsor branding. POST only. Its own url so the
   * ceiling is not shared with the save.
   */
  rideSponsorAsset: "/v1/admin/ride-sponsor-assets",
  /**
   * Winner selections from one activation's completed registrations.
   *
   * GET lists the live pool size and every past draw. POST records a new one.
   * Same path, different method, one rate-limit ceiling. Query and body both
   * carry activation_id, matching legalDraft.
   */
  draw: "/v1/admin/activation-draws",
  /**
   * Prize-wheel join kind and slice labels for one activation.
   *
   * GET reads the desk, defaulting to ordinary registration when no row has
   * been saved. PUT writes join kind and 4–12 labels. Same path, different
   * method, one rate-limit ceiling. Query and body both carry activation_id,
   * matching legalDraft.
   */
  wheel: "/v1/admin/activation-wheels",
  /**
   * The wheel's prizes, on top of its slices (156, decision C10): whether the
   * spin picks by weight, each slice's kind, weight and stock, the claim
   * code's prefix and lifetime, and the "Email the prize" switch.
   *
   * GET reads the desk with what each prize has given; PUT saves it. Same
   * path, two methods, one rate-limit ceiling. activation_id in the query and
   * the body, matching the wheel.
   */
  wheelPrizes: "/v1/admin/activation-wheel-prizes",
  /**
   * A prize handed over at the stand (156). POST with the activation and the
   * spin; one row in activation_wheel_collection naming who marked it. The url
   * names the collection, not the spin, as attendeeActions names the action.
   */
  wheelCollections: "/v1/admin/activation-wheel-collections",
  /**
   * Registration › Emails (160, decision C7): the passcode's words, the
   * registration confirmation, the post-show follow-ups, the sender each route
   * leaves from, the senders configured, and what happened to the mail.
   *
   * GET reads it all with the figures; PUT saves the whole page. Same path,
   * two methods, one rate-limit ceiling. activation_id in the query and the
   * body, matching the wheel.
   */
  showEmails: "/v1/admin/activation-emails",
  /**
   * A test of one of the page's emails, from its draft, to the signed-in
   * person's own address and nobody else's (160). POST; the body names the
   * show and which email, never a recipient.
   */
  showEmailTests: "/v1/admin/activation-email-tests",
  /**
   * The theme library: saved client palettes (147, admin/themes.ts).
   *
   * GET lists every live theme, Nova house first and locked, or with
   * ?client_id= the themes that client may wear; ?theme_id= reads one, archived
   * or not. POST creates, PUT updates by theme_id in the body, and DELETE
   * archives by ?theme_id= and clears every pointer to it. A save under the
   * contrast floor is a 422. Same path, four methods, one rate-limit ceiling.
   */
  themes: "/v1/admin/themes",
  /**
   * The theme a client's new shows start with. GET and PUT, client_id in the
   * query and in the body. A null theme_id is no default.
   */
  clientThemes: "/v1/admin/client-themes",
  /**
   * Which theme one show wears and which apps at it are pointed elsewhere.
   * GET also answers what every app resolves to, by resolveShowTheme, so the
   * console draws "Which surface wears what" without the rule. PUT writes the
   * show theme and the per-app pointers. activation_id in query and body,
   * matching the wheel.
   */
  activationThemes: "/v1/admin/activation-themes",
  /**
   * One action on one person's record (152, decision C4): withhold, release,
   * block, unblock, correct or erase. An Admin's, on a session, behind a
   * fresh authenticator code on the request, and one row in attendee_action.
   * A POST that creates the action, so the url names the action rather than
   * the person.
   */
  attendeeActions: "/v1/admin/attendee-actions",
  /**
   * Ask FortNine again for an erasure's Ride follow-up (153), where the first
   * attempt did not land. An Admin's; no fresh code, because nothing new is
   * decided.
   */
  attendeeErasureRetries: "/v1/admin/attendee-erasure-retries",
} as const;

/** What an Admin can do to one person's record (152). */
export const ATTENDEE_ACTIONS = ["withhold", "release", "block", "unblock", "correct", "erase"] as const;
export type AttendeeActionKind = (typeof ATTENDEE_ACTIONS)[number];

/** The contact slots a correction may move, and nothing else. */
export const CORRECTABLE_FIELDS = ["display_name", "contact_email", "deliver_to_email", "phone"] as const;
export type CorrectableField = (typeof CORRECTABLE_FIELDS)[number];

/**
 * The columns `GET /v1/admin/clients` will order by, as `?sort=<key>&dir=<asc|desc>`.
 *
 * ONE LIST, READ BY BOTH ENDS. The console's Clients table draws a sortable
 * head for each of these and nothing else, and the service refuses anything
 * else with a 400 rather than falling back to its own order: a request for an
 * order the server did not apply, answered in a different one, is a list the
 * reader believes is sorted by what they pressed. Each key is one of the five
 * columns the table draws (Client, Activations, Registrations, Retention,
 * Agreement), so "every column sorts" is the length of this array.
 *
 * The 12 October hand-off puts the sort on the server so it orders the whole
 * list rather than the rows on screen. people.ts maps each key to a fixed SQL
 * expression — the request names a key, never a column — and always finishes
 * on the name and then the id, so two companies with the same figure never
 * swap places between two reads.
 */
export const CLIENT_SORT_KEYS = ["name", "activations", "registrations", "retention", "agreement"] as const;
export type ClientSortKey = (typeof CLIENT_SORT_KEYS)[number];

/**
 * The Team list's columns, each sortable on the server (12 Oct hand-off), by
 * CLIENT_SORT_KEYS' rule: each key is one fixed SQL expression in team.ts, any
 * other value is a 400, and every order ends on the name and then the id.
 * `person` is the name, `clients` the first client a person holds (an Admin's
 * "every client" first), `signin` invited, then passcode only, then with an
 * authenticator, then by last seen.
 */
export const TEAM_SORT_KEYS = ["person", "access", "clients", "export", "signin"] as const;
export type TeamSortKey = (typeof TEAM_SORT_KEYS)[number];
